import { useCallback, useEffect, useRef, useState } from 'react';
import { orderBy } from 'lodash';
import moment from 'moment/moment';
import { type Scenario, Security, Value, WaterfallService } from 'api';
import { CapTableDetail } from 'pages/Waterfall/types';
import { useResponse } from 'services/hooks';
import { ISO_DATE_FORMAT, Logger, toString } from 'utilities';
import {
  UseWaterfallDistributionParams,
  UseWaterfallDistributionResult,
} from './useWaterfallDistribution.props';
import { WeightedShareValue } from '../../components/FundFirmDistribution/FundFirmDistribution.props';
import { calculateOwnersSharesValue } from '../../utilities';

export type { UseWaterfallDistributionParams, UseWaterfallDistributionResult } from './useWaterfallDistribution.props';

export const useWaterfallDistribution = ({
  selectedCaptable,
  equityValue,
  exitDate,
  captableDetail,
  capTableList,
  isInitializedFromUrl,
  isInitializingFromUrl,
  selectedCompanyId,
  fundList,
  childrenCompanies,
  childrenCapTables,
  validSecurities,
}: UseWaterfallDistributionParams): UseWaterfallDistributionResult => {
  const [scenarios, setScenarios] = useState<Scenario>();
  const [isLoading, setIsLoading] = useState(false);
  const [updatedWeightedShareValues, setUpdatedWeightedShareValues] = useState<WeightedShareValue[] | undefined>();

  const { errorNotification } = useResponse();
  const lastDistributionParams = useRef<string>('');
  const previousCompanyId = useRef<number | undefined>(undefined);
  const forceRefresh = useRef<boolean>(false);

  // Initialize previous company ID on first render
  useEffect(() => {
    if (selectedCompanyId && previousCompanyId.current === undefined) {
      previousCompanyId.current = selectedCompanyId;
    }
  }, [selectedCompanyId]);

  // ============================================================
  // Weighted Share Values Calculation
  // ============================================================
  const calculateWeightedShareValues = useCallback(
    (presentValues: Array<{ security: Security; value: number | string }>) => {
      if (!captableDetail || !childrenCompanies || !fundList || !captableDetail?.fund_ownership) {
        return;
      }

      const sortedPresentValues = orderBy(presentValues, ['security.id'], ['desc']);
      const fundOwnershipRef = captableDetail.fund_ownership;

      const ownersSharesValues = calculateOwnersSharesValue({
        sortedPresentValues: sortedPresentValues as Value[],
        capTableDetail: captableDetail as CapTableDetail,
        fundList,
        childrenCompanies,
        childrenCapTables,
        mainFundOwnershipRef: { current: fundOwnershipRef },
        entityReferenceId: null,
      });

      setUpdatedWeightedShareValues(ownersSharesValues);
    },
    [captableDetail, childrenCompanies, fundList, childrenCapTables]
  );

  // Initialize weighted share values with zero values when dependencies are ready
  useEffect(() => {
    if (
      captableDetail &&
      childrenCompanies &&
      fundList &&
      validSecurities.length > 0 &&
      captableDetail?.fund_ownership
    ) {
      const defaultPresentValues = validSecurities.map(security => ({
        security: { name: security.name, id: security.id as number },
        value: 0,
      }));

      calculateWeightedShareValues(defaultPresentValues);
    }
  }, [captableDetail, childrenCompanies, fundList, validSecurities, calculateWeightedShareValues]);

  // ============================================================
  // Distribution API Call
  // ============================================================
  const getDistribution = useCallback(async () => {
    // Do not proceed if the cap table is empty
    if (capTableList?.find(captable => captable.id === selectedCaptable)?.is_empty) {
      setScenarios(undefined);
      return;
    }

    const exitDateString = moment(exitDate).format(ISO_DATE_FORMAT);
    if (!selectedCaptable || !equityValue || !exitDateString) {
      setScenarios(undefined);
      return;
    }

    // Create a unique key for this distribution request to prevent duplicate calls
    // Include company ID to prevent cross-company conflicts
    const distributionKey = `${selectedCompanyId}-${selectedCaptable}-${equityValue}-${exitDateString}`;

    // Prevent duplicate calls with the same parameters, unless we're forcing a refresh
    if (lastDistributionParams.current === distributionKey && !forceRefresh.current) {
      return;
    }

    // Reset force refresh flag after using it
    forceRefresh.current = false;

    lastDistributionParams.current = distributionKey;

    try {
      setIsLoading(true);
      setScenarios(undefined);

      // `/api/waterfall` always runs liquidation waterfall math. Only
      // captableId, exitDate, and equityValue affect the calculation.
      // scenarioMethod / scenarioType are required by the codegen type but
      // ignored by WaterfallAllocationResultsSerializer.
      const scenarioValues = await WaterfallService.apiWaterfallCreate({
        captableId: selectedCaptable,
        equityValue: toString(equityValue),
        exitDate: exitDateString,
        scenarioMethod: 0,
        scenarioType: 1,
      });

      if (scenarioValues) {
        setScenarios(scenarioValues);
        // Calculate weighted share values from scenario results
        const { present_values: presentValues } = scenarioValues;
        if (presentValues) {
          calculateWeightedShareValues(presentValues as Array<{ security: { id: number, name: string }; value: string }>);
        }
      } else {
        setScenarios(undefined);
      }
    } catch (error: any) {
      let errorMessage = '';

      if (error.response?.text) {
        try {
          const errorMessageData = JSON.parse(error.response.text) as { [key: string]: string[] };
          // If it exists, error.response.text is an object with the actual message inside of an array
          const firstValue = Object.values(errorMessageData)[0];
          errorMessage = firstValue?.[0] ? firstValue[0] : error.response.statusText;
        } catch (parseError) {
          // If JSON parsing fails, use the fallback message
          errorMessage = 'An error occurred while processing the response';
          Logger.error('Error processing response', parseError);
        }
      }
      errorNotification(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [selectedCaptable, equityValue, exitDate, capTableList, calculateWeightedShareValues, errorNotification, selectedCompanyId]);

  // Reset distribution parameters when company changes
  useEffect(() => {
    if (selectedCompanyId && selectedCompanyId !== previousCompanyId.current) {
      previousCompanyId.current = selectedCompanyId;
      // Reset the distribution parameters to ensure fresh calculations for new company
      lastDistributionParams.current = '';
      setScenarios(undefined);
      forceRefresh.current = true; // Force refresh on company change
    }
  }, [selectedCompanyId]);

  // Also reset when cap table changes (in case it's a different company's cap table)
  useEffect(() => {
    if (selectedCaptable) {
      // Reset distribution parameters when cap table changes to ensure fresh calculations
      lastDistributionParams.current = '';
      setScenarios(undefined);
      forceRefresh.current = true; // Force refresh on cap table change
    }
  }, [selectedCaptable]);

  // Consolidated effect to handle distribution calls
  useEffect(() => {
    const shouldRunDistribution =
      captableDetail &&
      equityValue &&
      exitDate &&
      selectedCaptable &&
      (isInitializedFromUrl || !isInitializingFromUrl);

    if (shouldRunDistribution) {
      // Force refresh to ensure we always get fresh calculations
      forceRefresh.current = true;
      getDistribution();
    }
  }, [
    captableDetail,
    equityValue,
    exitDate,
    selectedCaptable,
    selectedCompanyId,
    isInitializedFromUrl,
    isInitializingFromUrl,
    getDistribution,
  ]);

  return {
    scenarios,
    setScenarios,
    isLoading,
    setIsLoading,
    getDistribution,
    updatedWeightedShareValues,
  };
};
